import { Avatar, Box, Button, Chip, Grid, LinearProgress, Typography } from "@mui/material"
import { Apis, getImageUrl, numberWithCommas } from "../../helpers"
import { Pie } from "./chart"
import { FC, useEffect, useState } from "react"
import { Connect, mapStateToProps } from "../../Redux"
import { PropertyProps } from "../../types/properties"
import NoData from "../Common/NoData"
import { useNavigate } from "react-router-dom"
import {
    AccountBalanceWalletOutlined,
    ApartmentOutlined,
    CalendarTodayOutlined,
    DoorFrontOutlined,
    HomeWorkOutlined,
    KingBedOutlined,
    LocationOnOutlined,
    MeetingRoomOutlined,
    PeopleOutlined,
    ReceiptOutlined,
    SavingsOutlined,
    SwapHorizOutlined,
    TrendingUpOutlined,
    WarningAmberOutlined,
    CurrencyExchangeOutlined,
} from "@mui/icons-material"
import FindUserModal, { FoundUser } from "../Collaborators/findUserModal"

// ── Brand palette ─────────────────────────────────────────────
const P       = '#8c7c0c'   // primary gold
const HERO_DARK = '#332a05' // deep gold-brown (hero gradient end, matches General)
const P_LIGHT = '#f5f2e0'   // cream
const P_BRIGHT= '#c4aa14'   // bright gold
const WARM_GREEN = '#6a8c0c' // greenish olive (success, adjacent to primary)
const WARM_AMBER = '#d4890a' // amber (warning)
const WARM_RED   = '#c05c13' // warm orange-red (arrears/bad)

// ── Types ──────────────────────────────────────────────────────
interface StatItem {
    label: string
    value: number
    bgColor?: string
    money?: boolean
    percentage?: boolean
    total?: number
    more?: { percentage?: number; label?: string; value?: number; money?: boolean }
}
interface Props { selectedProperty?: PropertyProps }
interface StateProps {
    loading: boolean
    firstRowItems: StatItem[]
    secondRowItems: StatItem[]
    pendingPayments?: number
    allTimeCollected: number
    recentPayments: any[]
    topArrears: any[]
    creditBalances: any[]
    totalCredit: number
    vacantUnits: any[]
}

// ── Card icon map ──────────────────────────────────────────────
const CARD_STYLES: Record<string, { icon: JSX.Element; color: string }> = {
    'Expected Rent':      { icon: <AccountBalanceWalletOutlined sx={{ fontSize: 19 }} />, color: P },
    'Rent Collected':     { icon: <TrendingUpOutlined sx={{ fontSize: 19 }} />,           color: WARM_GREEN },
    'BnBs Revenue':       { icon: <CurrencyExchangeOutlined sx={{ fontSize: 19 }} />,     color: P_BRIGHT },
    'Arrears':            { icon: <WarningAmberOutlined sx={{ fontSize: 19 }} />,         color: WARM_RED },
    'Total Units':        { icon: <ApartmentOutlined sx={{ fontSize: 19 }} />,            color: P },
    'Occupied Units':     { icon: <PeopleOutlined sx={{ fontSize: 19 }} />,               color: WARM_GREEN },
    'BnBs':               { icon: <KingBedOutlined sx={{ fontSize: 19 }} />,              color: P_BRIGHT },
    'Units with Arrears': { icon: <MeetingRoomOutlined sx={{ fontSize: 19 }} />,          color: WARM_RED },
}
const fallback = { icon: <HomeWorkOutlined sx={{ fontSize: 19 }} />, color: P }

// ── Reusable stat card ─────────────────────────────────────────
interface MetricCardProps {
    item: StatItem
    progressValue?: number
    progressColor?: string
    progressLabel?: string
    highlight?: boolean
}
const MetricCard: FC<MetricCardProps> = ({ item, progressValue, progressColor = P, progressLabel, highlight }) => {
    if (!item) return null
    const { icon, color } = CARD_STYLES[item.label] ?? fallback
    const pct = item.more?.percentage

    return (
        <Box sx={{
            bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, p: 2.5, height: '100%',
            display: 'flex', flexDirection: 'column', gap: 1.5,
            boxShadow: highlight ? `0 0 0 1.5px ${WARM_RED}` : 'none',
            transition: 'box-shadow .18s, transform .15s',
            '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,.08)', transform: 'translateY(-2px)' },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .7 }}>
                    {item.label}
                </Typography>
                <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: `${color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                    {icon}
                </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
                {item.money && <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', mr: 0.5 }}>Ksh</Typography>}
                <Typography component="span" sx={{ fontSize: 27, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                    {item.money ? numberWithCommas(item.value) : item.value}
                </Typography>
                {item.percentage && <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, color: '#374151', ml: 0.3 }}>%</Typography>}
            </Box>

            {progressValue !== undefined && (
                <Box>
                    <LinearProgress variant="determinate" value={Math.min(Math.max(progressValue, 0), 100)}
                        sx={{ height: 5, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 3 } }} />
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.5 }}>{progressLabel}</Typography>
                </Box>
            )}

            {pct !== undefined && progressValue === undefined && (
                <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 1.2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {Number.isFinite(pct) ? pct.toFixed(1) : '0.0'}%
                        {item.label.includes('Occupied') ? ' occupancy' : ''}
                        {item.label.includes('BnBs') && !item.label.includes('Revenue') ? ' of total' : ''}
                        {item.label.includes('Arrears') && !item.money ? ' have arrears' : ''}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

const SectionLabel: FC<{ children: string }> = ({ children }) => (
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: P, textTransform: 'uppercase', letterSpacing: .6, mb: 1.5, mt: 1 }}>
        {children}
    </Typography>
)

const initials = (name = '') => name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
const fmtDate = (d: any) => {
    try { return new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' }) } catch { return '' }
}

// ── Dashboard ──────────────────────────────────────────────────
const Dashboard: FC<Props> = ({ selectedProperty }) => {
    const [state, setState] = useState<StateProps>({
        firstRowItems: [], secondRowItems: [],
        loading: true, allTimeCollected: 0, recentPayments: [], topArrears: [],
        creditBalances: [], totalCredit: 0, vacantUnits: [],
    })
    const [heroImgFailed, setHeroImgFailed] = useState(false)
    const [showTransfer, setShowTransfer] = useState(false)
    const { firstRowItems, secondRowItems, loading, allTimeCollected, recentPayments, topArrears, creditBalances, totalCredit, vacantUnits } = state
    const navigate = useNavigate()

    const onTransferSelect = (user: FoundUser) => navigate('/add_collaborator', {
        state: { property: selectedProperty, collaborator: user, transfer: true }
    })

    useEffect(() => {
        if (!selectedProperty) { navigate('/properties'); return }
        const id = selectedProperty._id
        Promise.all([
            Apis({ module: 'Properties', action: 'getPropertyData', propertId: id }),
            Apis({ module: 'Payments', action: 'getPropertyPayments', propertId: id, page: 0, pageSize: 5 }),
            Apis({ module: 'Payments', action: 'getArrears', propertId: id, page: 0, pageSize: 5 }),
            Apis({ module: 'Properties', action: 'getCreditBalances', propertId: id, page: 0, pageSize: 5 }),
            Apis({ module: 'Properties', action: 'getVacantUnits', propertId: id, page: 0, pageSize: 5 }),
        ]).then(([propData, payments, arrears, credits, vacant]: any[]) => {
            setState(s => ({
                ...s, ...propData, recentPayments: payments || [], topArrears: arrears || [],
                creditBalances: credits?.credits || [], totalCredit: credits?.totalCredit || 0,
                vacantUnits: vacant?.units || [], loading: false
            }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false }))
        })
    }, [])

    // Derived
    const expected     = firstRowItems[0]?.value || 0
    const collected    = firstRowItems[1]?.value || 0
    const arrears      = firstRowItems[3]?.value || 0
    const totalUnits   = secondRowItems[0]?.value || 0
    const occupiedUnits= secondRowItems[1]?.value || 0
    const arrearsCount = (firstRowItems[3] as any)?.total ?? secondRowItems[3]?.value ?? 0
    const collectionRate= expected > 0 ? Math.round(collected * 100 / expected) : 0
    const occupancyRate = totalUnits   > 0 ? Math.round(occupiedUnits * 100 / totalUnits) : 0

    const collectColor = collectionRate >= 80 ? WARM_GREEN : collectionRate >= 50 ? WARM_AMBER : WARM_RED
    const occupColor   = occupancyRate  >= 80 ? P_BRIGHT   : occupancyRate  >= 50 ? WARM_AMBER : WARM_RED

    return (
        <Box sx={{ pb: 6, pr: { xs: 2, md: 4 }, minHeight: '100vh', bgcolor: '#f8f9fb' }}>

            {/* ── Hero ─────────────────────────────────────── */}
            <Box sx={{
                position: 'relative', overflow: 'hidden',
                background: `linear-gradient(135deg, ${P} 0%, ${HERO_DARK} 100%)`,
                borderRadius: 4, p: { xs: 3, md: 4 }, mb: 4,
            }}>
                {/* Decorative bubbles */}
                <Box sx={{
                    position: 'absolute', top: -60, right: -40, width: 220, height: 220, zIndex: 0,
                    borderRadius: '50%', bgcolor: 'rgba(255,255,255,.06)', pointerEvents: 'none',
                }} />
                <Box sx={{
                    position: 'absolute', bottom: -70, right: 90, width: 140, height: 140, zIndex: 0,
                    borderRadius: '50%', bgcolor: 'rgba(255,255,255,.05)', pointerEvents: 'none',
                }} />
                <Box sx={{
                    position: 'absolute', top: 40, right: 180, width: 60, height: 60, zIndex: 0,
                    borderRadius: '50%', bgcolor: 'rgba(255,255,255,.04)', pointerEvents: 'none',
                }} />
                <Box sx={{
                    position: 'absolute', bottom: -30, left: -30, width: 130, height: 130, zIndex: 0,
                    borderRadius: '50%', bgcolor: 'rgba(255,255,255,.04)', pointerEvents: 'none',
                }} />

                {/* Property name row */}
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    {(() => {
                        const heroLogoUrl = getImageUrl(selectedProperty?.logos, 'small')
                        const propInitials = (selectedProperty?.name || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                        const showHeroFallback = !heroLogoUrl || heroImgFailed
                        return (
                            <Box sx={{
                                width: 48, height: 48, borderRadius: 2, flexShrink: 0, overflow: 'hidden',
                                border: `2px solid rgba(196,170,20,.35)`,
                                bgcolor: P_LIGHT,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {!showHeroFallback
                                    ? <Box component="img" src={heroLogoUrl} alt={selectedProperty?.name}
                                        onError={() => setHeroImgFailed(true)}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    : <Typography sx={{ fontWeight: 800, fontSize: 16, color: P, lineHeight: 1, userSelect: 'none' }}>
                                        {propInitials}
                                      </Typography>
                                }
                            </Box>
                        )
                    })()}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                            {selectedProperty?.name}
                        </Typography>
                        {(selectedProperty?.placeName || selectedProperty?.location?.locName) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                <LocationOnOutlined sx={{ fontSize: 13, color: 'rgba(196,170,20,.6)' }} />
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>
                                    {[selectedProperty.placeName, selectedProperty.location?.locName].filter(Boolean).join(', ')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {selectedProperty?.role === 'Owner' && (
                        <Box
                            onClick={() => setShowTransfer(true)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.6, mr: 1,
                                borderRadius: 2, cursor: 'pointer', bgcolor: 'rgba(255,255,255,.08)',
                                border: '1px solid rgba(255,255,255,.15)', '&:hover': { bgcolor: 'rgba(255,255,255,.14)' }
                            }}
                        >
                            <SwapHorizOutlined sx={{ fontSize: 15, color: '#fff' }} />
                            <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>Transfer Property</Typography>
                        </Box>
                    )}
                    <Chip label="Live" size="small"
                        sx={{ bgcolor: `${P_BRIGHT}22`, color: P_BRIGHT, fontWeight: 700, border: `1px solid ${P_BRIGHT}44`, fontSize: 11 }} />
                </Box>

                {/* Key metrics */}
                {!loading && (
                    <Grid container spacing={{ xs: 3, md: 4 }} sx={{ position: 'relative', zIndex: 1 }}>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, mb: 1 }}>Occupancy</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                                <Typography sx={{ fontSize: 36, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{occupancyRate}</Typography>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,.6)' }}>%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={occupancyRate}
                                sx={{ mt: 1.5, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,.1)', '& .MuiLinearProgress-bar': { bgcolor: occupColor } }} />
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.35)', mt: 0.7 }}>{occupiedUnits} / {totalUnits} units</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, mb: 1 }}>Expected</Typography>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.35)', lineHeight: 1 }}>Ksh</Typography>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{numberWithCommas(expected)}</Typography>
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.35)', mt: 0.7 }}>This month</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, mb: 1 }}>Collected</Typography>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.35)', lineHeight: 1 }}>Ksh</Typography>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: collectionRate >= 80 ? P_BRIGHT : collectionRate >= 50 ? WARM_AMBER : '#f5a47a', lineHeight: 1.1 }}>
                                {numberWithCommas(collected)}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.35)', mt: 0.7 }}>{collectionRate}% of expected</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, mb: 1 }}>Arrears</Typography>
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.35)', lineHeight: 1 }}>Ksh</Typography>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: arrears > 0 ? '#f5a47a' : P_BRIGHT, lineHeight: 1.1 }}>
                                {numberWithCommas(arrears)}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.35)', mt: 0.7 }}>
                                {arrearsCount} {arrearsCount === 1 ? 'tenant' : 'tenants'} affected
                            </Typography>
                        </Grid>

                        {/* All-time collected — full width accent strip */}
                        <Grid item xs={12}>
                            <Box sx={{
                                mt: 1,
                                pt: 2.5,
                                borderTop: `1px solid rgba(196,170,20,.2)`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: `${P_BRIGHT}22`, border: `1px solid ${P_BRIGHT}44`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <TrendingUpOutlined sx={{ fontSize: 18, color: P_BRIGHT }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, lineHeight: 1 }}>
                                        Total Collected — All Time
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: `${P_BRIGHT}cc` }}>Ksh</Typography>
                                        <Typography sx={{ fontSize: 28, fontWeight: 900, color: P_BRIGHT, lineHeight: 1 }}>
                                            {numberWithCommas(allTimeCollected)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: 'rgba(106,140,12,.15)', border: '1px solid rgba(106,140,12,.35)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <SavingsOutlined sx={{ fontSize: 18, color: WARM_GREEN }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .9, lineHeight: 1 }}>
                                        Tenant Credit
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'rgba(106,140,12,.8)' }}>Ksh</Typography>
                                        <Typography sx={{ fontSize: 28, fontWeight: 900, color: WARM_GREEN, lineHeight: 1 }}>
                                            {numberWithCommas(totalCredit)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.3)', ml: { sm: 'auto' } }}>
                                    Since property was added
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {loading ? (
                <Box sx={{ height: '45vh' }}><NoData loading={loading} /></Box>
            ) : (
                <>
                    {/* ── Financial stats ─────────────────────── */}
                    <SectionLabel>Financial Overview</SectionLabel>
                    <Grid container spacing={2.5} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={firstRowItems[0]} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={firstRowItems[1]}
                                progressValue={collectionRate} progressColor={collectColor}
                                progressLabel={`${collectionRate}% of expected collected`} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={firstRowItems[2]} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={firstRowItems[3]} highlight={arrears > 0} />
                        </Grid>
                    </Grid>

                    {/* ── Occupancy stats ─────────────────────── */}
                    <SectionLabel>Occupancy</SectionLabel>
                    <Grid container spacing={2.5} sx={{ mb: 5 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={secondRowItems[0]} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={secondRowItems[1]}
                                progressValue={occupancyRate} progressColor={occupancyRate >= 80 ? P : WARM_AMBER}
                                progressLabel={`${occupancyRate}% occupancy rate`} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={secondRowItems[2]} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard item={secondRowItems[3]} highlight={(secondRowItems[3]?.value || 0) > 0} />
                        </Grid>
                    </Grid>

                    {/* ── Analytics ───────────────────────────── */}
                    <SectionLabel>Analytics</SectionLabel>
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, p: 3 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Revenue Breakdown</Typography>
                                <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.3, mb: 1 }}>Collected · Arrears · BnBs</Typography>
                                <Pie items={[
                                    { value: collected || 0, label: 'Collected', color: P },
                                    { value: arrears  || 0, label: 'Arrears',   color: WARM_RED },
                                    { value: firstRowItems[2]?.value || 0, label: 'BnBs', color: P_BRIGHT },
                                ]} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, p: 3 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Unit Distribution</Typography>
                                <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.3, mb: 1 }}>Occupied · Vacant · BnBs</Typography>
                                <Pie items={[
                                    { value: occupiedUnits || 0, label: 'Occupied', color: P },
                                    { value: Math.max((totalUnits || 0) - (occupiedUnits || 0) - (secondRowItems[2]?.value || 0), 0), label: 'Vacant', color: WARM_AMBER },
                                    { value: secondRowItems[2]?.value || 0, label: 'BnBs', color: P_BRIGHT },
                                ]} />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* ── Recent Payments + Top Arrears ──────── */}
                    <Grid container spacing={3}>
                        {/* Recent Payments */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Recent Payments</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.2 }}>Latest rent collections</Typography>
                                    </Box>
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: P_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P }}>
                                        <ReceiptOutlined sx={{ fontSize: 19 }} />
                                    </Box>
                                </Box>

                                {recentPayments.length === 0 ? (
                                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No payments recorded yet</Typography>
                                    </Box>
                                ) : (
                                    recentPayments.map((p: any, i: number) => (
                                        <Box key={i} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.8,
                                            borderBottom: i < recentPayments.length - 1 ? '1px solid #f8fafc' : 'none',
                                            '&:hover': { bgcolor: '#fafaf8' },
                                        }}>
                                            <Avatar src={p.tenant?.profilePhotos?.small?.url} sx={{ width: 36, height: 36, bgcolor: P_LIGHT, color: P, fontSize: 13, fontWeight: 700 }}>
                                                {initials(p.tenant?.name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }} noWrap>
                                                    {p.tenant?.name || 'Unknown Tenant'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.2 }}>
                                                    {p.unitLabel?.unitLabel || p.unit?.name || '—'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: WARM_GREEN }}>
                                                    Ksh {numberWithCommas(p.payment?.amount)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, justifyContent: 'flex-end', mt: 0.2 }}>
                                                    <CalendarTodayOutlined sx={{ fontSize: 10, color: '#cbd5e1' }} />
                                                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                        {fmtDate(p.payment?.dateOfPayment || p.dateAdded)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Grid>

                        {/* Top Arrears */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Tenants with Arrears</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.2 }}>Outstanding balances</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {topArrears.length > 0 && (
                                            <Button
                                                size="small"
                                                onClick={() => navigate('/arrears')}
                                                sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none', color: WARM_RED, minWidth: 0, px: 1 }}
                                            >
                                                View All
                                            </Button>
                                        )}
                                        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#fdf2ec', display: 'flex', alignItems: 'center', justifyContent: 'center', color: WARM_RED }}>
                                            <WarningAmberOutlined sx={{ fontSize: 19 }} />
                                        </Box>
                                    </Box>
                                </Box>

                                {topArrears.length === 0 ? (
                                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No arrears — great work! 🎉</Typography>
                                    </Box>
                                ) : (
                                    topArrears.map((a: any, i: number) => (
                                        <Box key={i} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.8,
                                            borderBottom: i < topArrears.length - 1 ? '1px solid #f8fafc' : 'none',
                                            '&:hover': { bgcolor: '#fdf9f8' },
                                        }}>
                                            <Avatar src={a.tenant?.profilePhotos?.small?.url} sx={{ width: 36, height: 36, bgcolor: '#fdf2ec', color: WARM_RED, fontSize: 13, fontWeight: 700 }}>
                                                {initials(a.tenant?.name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }} noWrap>
                                                    {a.tenant?.name || 'Unknown Tenant'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.2 }}>
                                                    {a.unitLabel?.unitLabel || a.unit?.name || '—'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: WARM_RED }}>
                                                    Ksh {numberWithCommas(a.amount)}
                                                </Typography>
                                                {a.dueDate && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, justifyContent: 'flex-end', mt: 0.2 }}>
                                                        <CalendarTodayOutlined sx={{ fontSize: 10, color: '#cbd5e1' }} />
                                                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                            Due {fmtDate(a.dueDate)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* ── Credit Balances + Vacant Units ─────── */}
                    <Grid container spacing={3} sx={{ mt: 3 }}>
                        {/* Credit Balances */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Tenants in Credit</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.2 }}>
                                            {totalCredit > 0 ? `Ksh ${numberWithCommas(totalCredit)} total credit` : 'Overpaid balances'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#f2f7e8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: WARM_GREEN }}>
                                        <SavingsOutlined sx={{ fontSize: 19 }} />
                                    </Box>
                                </Box>

                                {creditBalances.length === 0 ? (
                                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No tenants in credit</Typography>
                                    </Box>
                                ) : (
                                    creditBalances.map((c: any, i: number) => (
                                        <Box key={i} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.8,
                                            borderBottom: i < creditBalances.length - 1 ? '1px solid #f8fafc' : 'none',
                                            '&:hover': { bgcolor: '#fafaf8' },
                                        }}>
                                            <Avatar src={c.profilePhotos?.small?.url} sx={{ width: 36, height: 36, bgcolor: '#f2f7e8', color: WARM_GREEN, fontSize: 13, fontWeight: 700 }}>
                                                {initials(c.tenantName)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }} noWrap>
                                                    {c.tenantName?.trim() || 'Unknown Tenant'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.2 }}>
                                                    {c.unitLabel || c.unitName || '—'}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: WARM_GREEN, flexShrink: 0 }}>
                                                Ksh {numberWithCommas(c.balance)}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Grid>

                        {/* Vacant Units */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #eef0f3', borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Vacant Units</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.2 }}>Available for rent</Typography>
                                    </Box>
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#fdf8e0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P_BRIGHT }}>
                                        <DoorFrontOutlined sx={{ fontSize: 19 }} />
                                    </Box>
                                </Box>

                                {vacantUnits.length === 0 ? (
                                    <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No vacant units 🎉</Typography>
                                    </Box>
                                ) : (
                                    vacantUnits.map((v: any, i: number) => (
                                        <Box key={i} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.8,
                                            borderBottom: i < vacantUnits.length - 1 ? '1px solid #f8fafc' : 'none',
                                            '&:hover': { bgcolor: '#fafaf8' },
                                        }}>
                                            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: P_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P, flexShrink: 0 }}>
                                                <HomeWorkOutlined sx={{ fontSize: 17 }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }} noWrap>
                                                    {v.unitLabel || '—'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.2 }}>
                                                    {v.unitName || '—'}
                                                </Typography>
                                            </Box>
                                            {v.pricePerMonth ? (
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151', flexShrink: 0 }}>
                                                    Ksh {numberWithCommas(v.pricePerMonth)}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </>
            )}

            {showTransfer && (
                <FindUserModal
                    propertyId={selectedProperty?._id}
                    isCollaborator
                    close={() => setShowTransfer(false)}
                    onSelect={onTransferSelect}
                />
            )}
        </Box>
    )
}

export default Connect(mapStateToProps)(Dashboard)
